import { getBackofficeToken } from '@api/backoffice';
import { getStoredUser } from '@utils/userSession';

export interface CsvColumn<T> {
  header: string;
  accessor: (row: T) => string;
}

/**
 * Build a fetch-ready URL string. In dev the Vite proxy forwards `/api/…`
 * requests to the backend, so we must use a relative path (no origin).
 * In production VITE_API_URL is set and we prepend it.
 */
function buildUrl(path: string, params: Record<string, string>): string {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v)
  ).toString();
  const base = import.meta.env.VITE_API_URL || '';
  return `${base}${path}${qs ? `?${qs}` : ''}`;
}

/**
 * Download a CSV from a backend streaming export endpoint.
 * Attaches both the JWT and backoffice HMAC token.
 */
export async function downloadBackofficeCsv(
  path: string,
  params: Record<string, string>,
  filename: string
): Promise<void> {
  const url = buildUrl(path, params);

  const headers: Record<string, string> = {};
  const user = getStoredUser();
  if (user?.token) {
    headers['Authorization'] = `Bearer ${user.token}`;
  }
  const backofficeToken = getBackofficeToken();
  if (backofficeToken) {
    headers['X-Backoffice-Token'] = backofficeToken;
  }

  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }

  const blob = await response.blob();
  triggerDownload(blob, filename);
}

/**
 * Convert an in-memory array to CSV and trigger a browser download.
 * Used for client-side tables where all data is already loaded.
 */
export function exportArrayToCsv<T>(
  data: T[],
  columns: CsvColumn<T>[],
  filename: string
): void {
  const headerRow = columns.map((c) => escapeCsvField(c.header)).join(',');
  const dataRows = data.map((row) =>
    columns.map((c) => escapeCsvField(c.accessor(row))).join(',')
  );
  const bom = '\uFEFF';
  const csv = bom + [headerRow, ...dataRows].join('\r\n') + '\r\n';

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, filename);
}

function escapeCsvField(value: string | null | undefined): string {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
